import React, { ChangeEvent, useCallback, useState, useRef } from 'react';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { Row, Col, Button } from 'react-bootstrap';

import { addHours, format, parseISO } from 'date-fns';
import { FaCloudUploadAlt } from 'react-icons/fa';
import api from '../../services/api';

import { useToast } from '../../hooks/toast';

import { ContainerDados } from './styles';

import Loading from '../Loading';
import Input from '../Input';

import getResponseErrors from '../../utils/getResponseErrors';
import getValidationErrors from '../../utils/getValidationErrors';

import { IAnexo } from './index';

interface IFormBusca {
  AneUsuCodigo: number;
  AneData: Date;
  AneNome: string;
  arquivo: HTMLInputElement;
  DocTexto: string;
  Modelo: string;
}

type PacienteProps = {
  pacCodigo: number;
  tipo: string;
  handleDadosSalvos?(dadosDocumento: IAnexo): void;
};
const allowedFileTypes = ['jpg', 'jpeg', 'png', 'bmp', 'webp', 'pdf'];
const MAX_FILE_SIZE = 15728640; // 15MB
const FORMATED_MAX_FILE_SIZE = `${(MAX_FILE_SIZE / 1048576).toFixed(0)} MB`;

const DocumentoCadastro: React.FC<PacienteProps> = ({
  pacCodigo,
  tipo,
  handleDadosSalvos,
}) => {
  const { addToast } = useToast();
  const formRef = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [erroMessage, setErroMessage] = useState<string>();

  const initialData = {
    AneData: format(new Date(), 'yyyy-MM-dd'),
  };

  const [file, setFile] = useState<File>();

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setErroMessage(undefined);
      const fileExtension = e.target.files[0].name.split('.').pop();
      if (!e.target.files[0] || !fileExtension) {
        setErroMessage('Arquivo obrigatório');
        return;
      }
      if (!allowedFileTypes.includes(fileExtension.toLowerCase())) {
        setErroMessage(
          `Arquivo não permitido. Os anexos devem ser dos tipos ${allowedFileTypes.join(
            ', ',
          )}`,
        );
        return;
      }
      if (e.target.files[0].size > MAX_FILE_SIZE) {
        const tamanho = `${(e.target.files[0].size / 1048576).toFixed(2)} MB`;
        setErroMessage(
          `Tamanho do arquivo maior do que o permitido, ${tamanho}`,
        );
        return;
      }
      setFile(e.target.files[0]);
    }
  }, []);

  const handleSubmit = useCallback(
    async (dados: IFormBusca) => {
      try {
        if (erroMessage) {
          addToast({
            type: 'error',
            title: 'Verifque o arquivo',
            description: erroMessage,
          });
          return;
        }
        if (!file) {
          addToast({
            type: 'error',
            title: 'Arquivo obrigatório',
          });
          return;
        }
        setLoading(true);
        formRef.current?.setErrors({});
        const schema = Yup.object().shape({
          AneData: Yup.date().required('Data é obrigatório'),
          AneNome: Yup.string().required('Título é obrigatório'),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        const response = await api.post<IAnexo>(`/anexos`, {
          ...dados,
          AneData: addHours(parseISO(dados.AneData.toString()), 4),
          AneNome: dados.AneNome.toUpperCase(),
          AnePacCodigo: pacCodigo,
          AneTipo: tipo,
        });

        const data = new FormData();
        if (file) data.append('arquivo', file);
        const responseAnexo = await api.patch(
          `/anexos/${response.data.AneCodigo}`,
          data,
        );

        if (handleDadosSalvos) handleDadosSalvos(responseAnexo.data);
      } catch (error) {
        console.log('erro:', error);
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRef.current?.setErrors(errors);
        } else {
          const mensagem = getResponseErrors(error);
          addToast({
            type: 'error',
            title: 'Não foi possível carregar dados',
            description: mensagem,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [erroMessage, pacCodigo, tipo, file, handleDadosSalvos, addToast],
  );

  return (
    <>
      {loading && <Loading />}
      <ContainerDados className="rounded">
        <Form ref={formRef} initialData={initialData} onSubmit={handleSubmit}>
          <Row>
            <Col sm={12}>
              <h5>Upload de {tipo.toLowerCase()}</h5>
            </Col>
            <Col md={5}>
              <Input name="AneNome" label="Descrição" required />
            </Col>
            <Col md={3}>
              <Input name="AneData" label="Data" type="date" required />
            </Col>
            <Col md={4} />
            <Col md={10} lg={8} className="mt-2">
              <div className="border rounded p-2">
                <b>Arquivo</b>
                <FaCloudUploadAlt size={18} />
                <input
                  type="file"
                  id="arquivo"
                  className="d-block w-100"
                  onChange={handleFileChange}
                  accept={`.${allowedFileTypes.join(', .')}`}
                />
                <div className="d-flex justify-content-between mt-2">
                  <div>Tamanho Limite: {FORMATED_MAX_FILE_SIZE}</div>
                  <div>
                    Extensões Permitidas: {`.${allowedFileTypes.join(', .')}`}
                  </div>
                </div>
                {erroMessage && (
                  <p className="text-danger pt-1 mb-0">* {erroMessage}</p>
                )}
              </div>
            </Col>

            <Col md={12} className="mt-2">
              <Button
                type="submit"
                variant="success"
                size="sm"
                disabled={loading}
              >
                Salvar Anexo
              </Button>
            </Col>
          </Row>
        </Form>
      </ContainerDados>
    </>
  );
};

export default DocumentoCadastro;
